import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Alert,
} from "@mui/material";
import AppForm, { AppFormSection } from '@/__core/components/AppForm';
import AutoSelector from '@/__core/components/AutoSelector';
import { useGroups } from "../hooks/useTasks";
import { getEmployees } from '@/modules/employees/redux/EmployeeCRUD';
import { useFormLoading } from "@/__core/helpers/Loading";
import { formatErrorForSnackbar, ApiError } from "@/__core/helpers/Error";
import MessageSnackbar from "@/__core/helpers/components/MessageSnackbar";

interface GroupCreationDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (groupData: any) => void;
  loading?: boolean;
  companyId?: string;
}

const GroupCreationDialog: React.FC<GroupCreationDialogProps> = ({
  open,
  onClose,
  onSave,
  loading: externalLoading = false,
  companyId,
}) => {
  const [groupData, setGroupData] = useState({
    display_name: '',
    description: '',
    member_ids: [] as string[],
  });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [employeeOptions, setEmployeeOptions] = useState<Array<{ id: string; name: string }>>([]);
  const [loadingEmployeeOptions, setLoadingEmployeeOptions] = useState(false);
  
  const { createGroup } = useGroups();
  const { loading: formLoading, handleSubmit } = useFormLoading();

  useEffect(() => {
    if (!open || !companyId) {
      setEmployeeOptions([]);
      setLoadingEmployeeOptions(false);
      return;
    }

    let isMounted = true;

    const fetchEmployees = async () => {
      try {
        setLoadingEmployeeOptions(true);
        const response = await getEmployees({
          company: companyId,
          status: 'PROBATION,WORKING',
          attendance_type: 'attendance',
          no_pagination: true,
        });

        const rawRows = Array.isArray(response?.data)
          ? response.data
          : (response?.data?.results || []);

        const mapByUserId = new Map<string, { id: string; name: string }>();

        rawRows.forEach((employee: any) => {
          const userId = String(employee?.user?.id || '').trim();
          if (!userId) return;

          const displayName =
            String(employee?.nickname || '').trim() ||
            String(employee?.name || '').trim() ||
            String(employee?.user?.name || '').trim() ||
            String(employee?.user?.email || '').trim() ||
            `User ${userId.slice(0, 8)}`;

          mapByUserId.set(userId, { id: userId, name: displayName });
        });

        if (isMounted) {
          setEmployeeOptions(Array.from(mapByUserId.values()));
        }
      } catch {
        if (isMounted) {
          setEmployeeOptions([]);
        }
      } finally {
        if (isMounted) {
          setLoadingEmployeeOptions(false);
        }
      }
    };

    fetchEmployees();

    return () => {
      isMounted = false;
    };
  }, [open, companyId]);

  // Clear messages when dialog opens
  useEffect(() => {
    if (open) {
      setError(null);
      setSuccess(null);
    }
  }, [open]);

  const handleSave = async () => {
    if (!groupData.display_name.trim()) {
      setError("Vui lòng nhập tên nhóm");
      return;
    }

    await handleSubmit(
      async () => {
        const createGroupData = {
          display_name: groupData.display_name.trim(),
          description: groupData.description.trim(),
          member_ids: groupData.member_ids,
          company: companyId || undefined,
        };

        const newGroup = await createGroup(createGroupData);
        return newGroup;
      },
      (result) => {
        // Success callback
        setSuccess(`Đã tạo nhóm "${groupData.display_name.trim()}" thành công!`);
        onSave(result);
        
        // Reset form
        resetForm();
        onClose();
      },
      (error: ApiError) => {
        // Error callback
        const errorMessage = formatErrorForSnackbar(error);
        setError(errorMessage);
      }
    );
  };

  const resetForm = () => {
    setGroupData({
      display_name: '',
      description: '',
      member_ids: [],
    });
    setError(null);
    setSuccess(null);
  };

  const handleClose = () => {
    // Reset form when closing
    resetForm();
    onClose();
  };

  const handleErrorClose = () => {
    setError(null);
  };

  const handleSuccessClose = () => {
    setSuccess(null);
  };

  const handleInputChange = (key: string, value: any) => {
    setGroupData((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const userOptions = useMemo(() => employeeOptions, [employeeOptions]);

  const sections: AppFormSection[] = [
    {
      key: 'group-form',
      fields: [
        {
          key: 'display_name',
          label: 'Tên nhóm',
          type: 'text',
          required: true,
          placeholder: 'Nhập tên nhóm',
          gridSize: { xs: 12 },
          labelWidth: 120,
        },
        {
          key: 'description',
          label: 'Mô tả',
          type: 'text',
          placeholder: 'Nhập mô tả nhóm (tùy chọn)',
          gridSize: { xs: 12 },
          labelWidth: 120,
        },
        {
          key: 'member_ids',
          label: '',
          type: 'custom',
          standalone: true,
          gridSize: { xs: 12 },
          render: ({ values, onChange }) => (
            <AutoSelector
              value={Array.isArray(values.member_ids) ? values.member_ids : []}
              onChange={(ids) => onChange('member_ids', ids)}
              label='Thành viên'
              labelWidth={120}
              multiple
              loading={loadingEmployeeOptions}
              noOptionsText={companyId ? 'Không có thành viên' : 'Chưa có công ty để tải nhân viên'}
              placeholder={companyId ? 'Chọn thành viên cho nhóm' : 'Chưa có công ty'}
              options={userOptions}
            />
          ),
        },
      ],
    },
  ];

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Tạo nhóm mới</DialogTitle>
        <DialogContent>
          {error ? <Alert severity='error' sx={{ mb: 2 }}>{error}</Alert> : null}
          <AppForm
            sections={sections}
            values={groupData}
            onChange={handleInputChange}
            onSubmit={handleSave}
            onCancel={handleClose}
            submitting={formLoading || externalLoading}
            submitDisabled={formLoading || externalLoading || !groupData.display_name.trim()}
            submitLabel='Tạo nhóm'
            submittingLabel='Đang tạo...'
            cancelLabel='Hủy'
            containerSx={{ p: 0, m: 0, width: '100%', backgroundColor: 'transparent' }}
          />
        </DialogContent>
      </Dialog>

      <MessageSnackbar
        open={!!error}
        message={error}
        severity="error"
        onClose={handleErrorClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      <MessageSnackbar
        open={!!success}
        message={success}
        severity="success"
        onClose={handleSuccessClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
};

export default GroupCreationDialog;
